// @ts-nocheck
import ConfirmationPanel from 'components/form/confirmation/confirmation-panel';
import React, { useEffect, useState } from 'react';
import { archiveInfo, assignRankingAndKeys } from 'services/utils/demographic-service';
import { AxiosPromise } from 'axios';
import { buildQaId } from 'utils/build-qa-id';
import { PatientUtil } from 'utils/patient-util';
import {
  change,
  FieldArray,
  getFormValues,
  InjectedFormProps,
  reduxForm,
  isDirty,
} from 'redux-form';
import { compose } from 'recompose';
import { editPatient, updatePatientPreferences } from 'actions/action-patient';
import { getModalStyle } from 'services/utils/styles-service';
import { Grid, Modal, Typography } from '@mui/material';
import ConfirmationDialogPanel from 'components/form/confirmation/confirmation-dialog-panel';
import withStyles from '@mui/styles/withStyles';
import { IState } from 'interfaces/redux/IState';
import { nameOfFactory } from 'utils/types-util';
import { RenderAddressesForContacts } from 'components/form/subform/subform';
import { connect, useDispatch, useSelector } from 'react-redux';
import { editContactAndUpdatePatient } from 'actions/action-contacts';
import { styles } from '../add-address-modal/add-address-modal.styles';
import { IProps } from './interfaces/IProps';
import { IFormProps } from './interfaces/IFormProps';

const PATIENT_DELIVERY_CONTACT_ID = 0;

const UNIQUE_CONTACT_TYPE_ID_FOR_PATIENT_KEY = 'patient';
const UNIQUE_CONTACT_TYPE_ID_FOR_CONTACT_KEY = 'contact';

const FORM_ID = 'edit-contact-address-modal';

// #region interfaces for the component
interface IEditContactAdressesModalProps extends IProps, InjectedFormProps<IFormProps> {}
interface IStateProps {
  initialValues: Partial<IFormProps>;
}

type Props = IStateProps &
  IEditContactAdressesModalProps & {
    visible: boolean;
    onModalClose: () => void;
  };
// #endregion

// #region constant things
const qaIdBuilder = buildQaId(FORM_ID);
const nameOfFormFields = nameOfFactory<IFormProps>();

// #endregion

const EditContactAddressModal: React.FC<Props> = (props: any): JSX.Element => {
  const { classes, formValues } = props;

  // #region component state
  const [errorMessage, setErrorMessage] = React.useState<string>('');
  const [saveDisabled, setSaveDisabled] = React.useState<boolean>(false);
  const [showSetAsPreferredRxDelivery, setShowSetAsPreferredRxDelivery] =
    React.useState<boolean>(false);
  const [showSetAsPreferredPatientAddress, setShowSetAsPreferredPatientAddress] =
    React.useState<boolean>(false);
  const [dialogSettings, setDialogSettings] = React.useState<any>({});
  const indexOfSelectionOfNewAddresses = React.useRef(null);
  const selectionIndex = React.useRef(formValues.preferred_address_index);
  const setSelectionAsTheNewPreferredRxDeliveryAddress = React.useRef(false);
  const setSelectionAsTheNewPrimaryAddress = React.useRef(false);
  const [_render, reRender] = React.useState<number>(0);
  const addedAddresses = React.useRef([]);
  // #endregion

  // #region redux
  const patient = useSelector((state: IState) => state.patient);
  const dispatch = useDispatch();
  // #endregion

  // #region functions
  const handleCancel = (): void => {
    setErrorMessage('');
    indexOfSelectionOfNewAddresses.current = null;
    setSelectionAsTheNewPrimaryAddress.current = null;
    setSelectionAsTheNewPreferredRxDeliveryAddress.current = false;
    addedAddresses.current = [];
    selectionIndex.current = -1;
    setSaveDisabled(false);
    props.onModalClose();
    props.reset();
  };

  const checkIfShouldBecomeTheNewPreferredRxDeliveryAddress = async () => {
    const values = formValues;
    if (!values || !values.addresses || values?.addresses?.length === 0) {
      return false;
    }
    const newAddresses = (values.addresses || [])
      .map((item, index) => ({ ...item, overAllIndex: index }))
      .filter(addr => !addr.deleted && !addr.key);
    addedAddresses.current = newAddresses;
    if (newAddresses.length === 0) {
      return true;
    }
    const userSelectedOneOfTheNewAddresses = newAddresses.some(
      item => item.overAllIndex === values.preferred_address_index,
    );
    if (userSelectedOneOfTheNewAddresses) {
      selectionIndex.current = values.preferred_address_index;
      return true;
    }
    return new Promise((fn_continue, _fn_cancel) => {
      setShowSetAsPreferredRxDelivery(true);
      setDialogSettings({
        dialogCancel: () => {
          setDialogSettings({});
          setShowSetAsPreferredRxDelivery(false);
          fn_continue(false);
        },
        dialogContinue: async () => {
          const sna = newAddresses[indexOfSelectionOfNewAddresses.current];
          setDialogSettings({});
          setShowSetAsPreferredRxDelivery(false);
          selectionIndex.current = formValues.addresses.findIndex(
            addr =>
              addr.city === sna.city &&
              addr.line1 === sna.line1 &&
              addr.zip === sna.zip &&
              addr.state === sna.state &&
              addr.use === sna.use &&
              addr.line2 == sna.line2,
          );
          fn_continue(true);
        },
      });
    });
  };

  const checkIfShouldBecomeTheNewPrimaryPatientAddress = async () => {
    const values = formValues;
    if (!values || !values.addresses || values?.addresses?.length === 0) {
      return false;
    }
    return new Promise((fn_continue, _fn_cancel) => {
      setShowSetAsPreferredPatientAddress(true);
      setDialogSettings({
        dialogCancel: () => {
          setDialogSettings({});
          setShowSetAsPreferredPatientAddress(false);
          fn_continue(false);
        },
        dialogContinue: async () => {
          setDialogSettings({});
          setShowSetAsPreferredPatientAddress(false);

          fn_continue(true);
        },
      });
    });
  };

  const handleSubmit = async (formValues: any, setAsNewPrefRxDeliveryAddress) => {
    setSaveDisabled(true);

    const contactEditPayloads = [];
    const contactEditPromises = [];

    const ensureUniqueKeys = (list: any[]) => {
      const seenKeys = new Set<number>();
      return list.map(addr => {
        if (addr.key && seenKeys.has(addr.key)) {
          const { key, ...rest } = addr;
          return rest;
        }
        if (addr.key) {
          seenKeys.add(addr.key);
        }
        return addr;
      });
    };

    const patientAddresses = ensureUniqueKeys(
      formValues.addresses
        .filter(({ ownerType }) => ownerType === UNIQUE_CONTACT_TYPE_ID_FOR_PATIENT_KEY)
        .map(({ ownerType, ownerId, ...address }) => {
          const wasFromPatientInitially = (patient.addresses || []).some(
            pa => pa.key === address.key,
          );

          if (!wasFromPatientInitially) {
            const { key, ...rest } = address;
            return rest;
          }
          return address;
        }),
    );

    const contactAddresses = formValues.addresses.filter(
      ({ ownerType }) => ownerType === UNIQUE_CONTACT_TYPE_ID_FOR_CONTACT_KEY,
    );

    const initialContactAddresses = props.initialValues.addresses?.filter(
      ({ ownerType }) => ownerType === UNIQUE_CONTACT_TYPE_ID_FOR_CONTACT_KEY,
    );

    const contactPayloads: Record<string, Array<any>> = {};

    contactAddresses.forEach(({ ownerId, ownerType, ...newContactAddress }) => {
      const matches = ownerId.match(/(\d+)$/);
      const contactKeyId = matches ? Number(matches[1]) : null;

      if (contactKeyId) {
        if (!contactPayloads[contactKeyId]) {
          contactPayloads[contactKeyId] = [newContactAddress];
        } else {
          contactPayloads[contactKeyId].push(newContactAddress);
        }
      }
    });

    const initialContactIdsWithAddresses = (initialContactAddresses || []).reduce(
      (acc: Set<number>, addressInfo: any) => {
        const matches = (addressInfo.ownerId || '').match(/(\d+)$/);
        if (matches) {
          acc.add(Number(matches[1]));
        }
        return acc;
      },
      new Set<number>(),
    );

    initialContactIdsWithAddresses.forEach(contactId => {
      if (!contactPayloads[contactId]) contactPayloads[contactId] = [];
    });

    Object.entries(contactPayloads).forEach(([contactId, contactAddressesAux]) => {
      const foundInitFormContactAddresses = initialContactAddresses
        .filter(contactInfo => {
          const matches = contactInfo.ownerId.match(/(\d+)$/);
          if (matches) {
            const contactKeyId = Number(matches[1]);
            return contactKeyId === Number(contactId);
          }
          return false;
        })
        .reduce((acc, addressInfo) => {
          const { ownerId, ownerType, ...restAddressInfo } = addressInfo;
          return [...acc, restAddressInfo];
        }, []);

      const cleanedContactAddresses = ensureUniqueKeys(
        contactAddressesAux.map(addr => {
          const keyExistsInitially = (foundInitFormContactAddresses || []).some(
            initFormContactAddress => initFormContactAddress.key === addr.key,
          );
          if (!keyExistsInitially) {
            const { key, ...rest } = addr;
            return rest;
          }
          return addr;
        }),
      );

      const contactEditPayload = {
        id: contactId,
        patient_id: props.patient.id,
        addresses: JSON.stringify(
          assignRankingAndKeys(
            archiveInfo(cleanedContactAddresses, foundInitFormContactAddresses || []),
            null,
          ),
        ),
      };

      contactEditPayloads.push(contactEditPayload);
      contactEditPromises.push(
        dispatch(editContactAndUpdatePatient(contactEditPayload) as unknown) as AxiosPromise<any>,
      );
    });

    const patientPreferencesEditPayload = {
      patient_id: patient.id,
      is_declined_to_provide_addresses: formValues.is_declined_to_provide_addresses,
      preferred_rx_delivery_patient:
        !formValues.preferred_rx_delivery_contact_id ||
        formValues.preferred_rx_delivery_contact_id === PATIENT_DELIVERY_CONTACT_ID,
      // ID of the contact, or null if its patient
      preferred_rx_delivery_contact_id:
        formValues.preferred_rx_delivery_contact_id === PATIENT_DELIVERY_CONTACT_ID
          ? null
          : formValues.preferred_rx_delivery_contact_id,
      // Index of the contacts in the list to show, patient address index or contact address index
      preferred_rx_delivery_entity_key: formValues.preferred_rx_delivery_entity_key,
      preferred_rx_delivery_method: formValues.preferred_rx_delivery_method,
    };

    const patientEditPayload = {
      id: props.patient.id,
    };

    if (patientAddresses && patientAddresses.length > 0) {
      patientEditPayload.addresses =
        patientAddresses && patientAddresses.length > 0
          ? JSON.stringify(
              assignRankingAndKeys(archiveInfo(patientAddresses as [], patient.addresses), null),
            )
          : null;
    }

    if (patientAddresses && patientAddresses.length === 0 && patient.addresses?.length > 0) {
      // Do we need to remove patient address when they are reassign to a contact?
      patientEditPayload.addresses = JSON.stringify(
        assignRankingAndKeys(archiveInfo([], patient.addresses), null),
      );
    }
    if (setSelectionAsTheNewPrimaryAddress.current) {
      const temp = [...JSON.parse(patientEditPayload.addresses)];
      const sna = formValues.addresses[selectionIndex.current];
      const selected_index = temp.findIndex(
        addr =>
          addr.city === sna.city &&
          addr.line1 === sna.line1 &&
          addr.zip === sna.zip &&
          addr.state === sna.state &&
          addr.use === sna.use &&
          addr.line2 == sna.line2,
      );
      patientEditPayload.addresses = JSON.stringify(
        assignRankingAndKeys(
          archiveInfo(patientAddresses as [], patient.addresses),
          selected_index,
        ),
      );
    }

    if (setSelectionAsTheNewPreferredRxDeliveryAddress.current) {
      const newPref = formValues.addresses[selectionIndex.current];
      const newPrefType = newPref.ownerType;
      const newPrefEntityId = (newPref.ownerId || '').split('_')[1];

      if (newPrefType === 'patient') {
        let newPrefAddressKey;
        try {
          newPrefAddressKey = (JSON.parse(patientEditPayload.addresses) || []).find(
            item =>
              !item.deleted &&
              item.line1 === newPref.line1 &&
              item.line2 == newPref.line2 &&
              item.city === newPref.city &&
              item.state === newPref.state &&
              item.zip === newPref.zip &&
              item.use === newPref.use,
          )?.key;
        } catch (e) {
          console.log('error parsing patientEditPayload.addresses ', e);
        }
        patientPreferencesEditPayload.preferred_rx_delivery_patient = 1;
        patientPreferencesEditPayload.preferred_rx_delivery_contact_id = null;
        patientPreferencesEditPayload.preferred_rx_delivery_entity_key = newPrefAddressKey;
      }
      if (newPrefType === 'contact') {
        let newPrefAddressKey;
        try {
          const targetContact = (contactEditPayloads || []).find(
            entry => entry.id === newPrefEntityId,
          );
          newPrefAddressKey = (JSON.parse(targetContact.addresses) || []).find(
            item =>
              !item.deleted &&
              item.line1 === newPref.line1 &&
              item.line2 == newPref.line2 &&
              item.city === newPref.city &&
              item.state === newPref.state &&
              item.zip === newPref.zip &&
              item.use === newPref.use,
          )?.key;
        } catch (e) {
          console.log('error parsing contactEditPayload.addresses ', e);
        }
        patientPreferencesEditPayload.preferred_rx_delivery_patient = 0;
        patientPreferencesEditPayload.preferred_rx_delivery_contact_id = newPrefEntityId;
        patientPreferencesEditPayload.preferred_rx_delivery_entity_key = newPrefAddressKey;
      }
    }

    if (formValues.addresses.length === 0) {
      patientPreferencesEditPayload.preferred_rx_delivery_patient = 1;
      patientPreferencesEditPayload.preferred_rx_delivery_contact_id = null;
      patientPreferencesEditPayload.preferred_rx_delivery_entity_key = null;
    }

    Promise.all([
      dispatch(
        editPatient({
          ...patientEditPayload,
          ...patientPreferencesEditPayload,
        }),
      ) as unknown as Promise<object>,
      ...contactEditPromises,
    ])
      .then(() => {
        handleCancel();
      })
      .catch(error => {
        setSaveDisabled(false);
        setErrorMessage('Could not update address information');
      });
  };

  const submitWithPrechecks = async e => {
    if (!props.invalid) {
      if (formValues.addresses.length > 0) {
        selectionIndex.current = formValues.preferred_address_index;
        if (formValues.addresses.length > 1) {
          checkIfShouldBecomeTheNewPreferredRxDeliveryAddress().then(result => {
            if (result && indexOfSelectionOfNewAddresses.current > 0) {
              const computedIndex =
                formValues.addresses.length -
                addedAddresses.current.length +
                indexOfSelectionOfNewAddresses.current;
              selectionIndex.current = computedIndex;
            }
            setSelectionAsTheNewPreferredRxDeliveryAddress.current = result;
            const selectedRxDeliveryAddress = formValues.addresses[selectionIndex.current];
            const selectedRxDeliveryAddressType = selectedRxDeliveryAddress.ownerType;

            if (selectedRxDeliveryAddressType === 'contact') {
              setSelectionAsTheNewPrimaryAddress.current = false;
              props.handleSubmit(handleSubmit)(e);
            } else if (selectedRxDeliveryAddressType === 'patient') {
              if (selectedRxDeliveryAddress.key !== patient.addresses?.[0]?.key) {
                checkIfShouldBecomeTheNewPrimaryPatientAddress().then(result => {
                  setSelectionAsTheNewPrimaryAddress.current = result;
                  props.handleSubmit(handleSubmit)(e);
                });
              } else {
                setSelectionAsTheNewPrimaryAddress.current = true;
                props.handleSubmit(handleSubmit)(e);
              }
            }
          });
        } else {
          setSelectionAsTheNewPreferredRxDeliveryAddress.current = true;
          setSelectionAsTheNewPrimaryAddress.current = true;
          selectionIndex.current = 0;
          props.handleSubmit(handleSubmit)(e);
        }
      } else {
        props.handleSubmit(handleSubmit)(e);
      }
    } else {
      const newAddresses = formValues.addresses
        .map((addr, index) => (addr.key === undefined ? index : null))
        .filter(addr => addr !== null);
      const fields_to_validate_arr = [];
      newAddresses.forEach(index => {
        fields_to_validate_arr.push(`addresses[${index}].use`);
        fields_to_validate_arr.push(`addresses[${index}].line1`);
        fields_to_validate_arr.push(`addresses[${index}].city`);
        fields_to_validate_arr.push(`addresses[${index}].state`);
        fields_to_validate_arr.push(`addresses[${index}].zip`);
      });
      dispatch(props.touch(props.form, ...fields_to_validate_arr));
    }
  };

  const p_contact_content = () => {
    return (
      <>
        <Typography gutterBottom className={classes.new_preferred_rx_delivery_question}>
          Should this also be set as the patient&apos;s primary contact address?
        </Typography>
      </>
    );
  };

  const p_rx_del_content = () => {
    const singleAddress = addedAddresses.current[0] || {};
    if (addedAddresses.current.length === 1) {
      indexOfSelectionOfNewAddresses.current = 0;
    }

    return (
      <>
        <Typography gutterBottom className={classes.new_preferred_rx_delivery_question}>
          {addedAddresses.current.length === 1
            ? 'Should this new address be saved as the Preferred Rx Delivery Address?'
            : 'Should one of these new addresses be saved as the Preferred Rx Delivery Address?'}
        </Typography>
        {addedAddresses.current.length > 1 ? (
          addedAddresses.current.map((addr, index) => {
            return (
              <div className={classes.new_preferred_rx_delivery_option}>
                <input
                  className={classes.new_preferred_rx_delivery_radio_button}
                  type="radio"
                  id={`new_delivery_address_choice_${index}`}
                  name="new_delivery_address_selection"
                  value={index}
                  onChange={() => {
                    selectionIndex.current = addr.overAllIndex;
                    indexOfSelectionOfNewAddresses.current = index;
                    reRender(_render + 1);
                  }}
                  checked={index === indexOfSelectionOfNewAddresses.current}
                />
                <label
                  htmlFor={`new_delivery_address_choice_${index}`}
                  className={classes.new_preferred_rx_delivery_address}
                >
                  {addr.line1}, {addr.line2 ? `${addr.line2}, ` : ''}
                  {addr.city}, {addr.state}, {addr.zip}
                </label>
              </div>
            );
          })
        ) : (
          <div className={classes.new_preferred_rx_delivery_address}>
            {singleAddress.line1}, {singleAddress.line2 ? `${singleAddress.line2}, ` : ''}
            {singleAddress.city}, {singleAddress.state}, {singleAddress.zip}
          </div>
        )}
      </>
    );
  };

  // #endregion

  return (
    <Modal open={props.visible} data-qa-id={qaIdBuilder('modal')}>
      <>
        {showSetAsPreferredRxDelivery ? (
          <ConfirmationDialogPanel
            open
            title="Save as Preferred Rx Delivery Address?"
            styleOverrides={{ buttonContainer: { justifyContent: 'space-evenly' } }}
            componentOverrides={{ noButtonVariant: 'contained' }}
            content={p_rx_del_content()}
            cancelText="No"
            continueText="Yes"
            onCancel={dialogSettings.dialogCancel}
            onContinue={dialogSettings.dialogContinue}
            disableContinueButton={
              indexOfSelectionOfNewAddresses.current === null && addedAddresses.length > 1
            }
          />
        ) : null}
        {showSetAsPreferredPatientAddress ? (
          <ConfirmationDialogPanel
            open
            title="Save as Patient's Preferred Contact Address?"
            styleOverrides={{ buttonContainer: { justifyContent: 'space-evenly' } }}
            componentOverrides={{ noButtonVariant: 'contained' }}
            content={p_contact_content()}
            cancelText="No"
            continueText="Yes"
            onCancel={dialogSettings.dialogCancel}
            onContinue={dialogSettings.dialogContinue}
            disableContinueButton={false}
          />
        ) : null}
        <form data-qa-id={qaIdBuilder(FORM_ID)} autoComplete="off">
          <div
            style={{ ...getModalStyle(), maxHeight: '50rem', overflowY: 'scroll', width: '900px' }}
            className={classes.addModal}
          >
            <Grid container>
              {errorMessage && (
                <Grid item xs={12} className={classes.row}>
                  <Typography className={classes.error}>{errorMessage}</Typography>
                </Grid>
              )}
            </Grid>
            <Grid item xs={12} className={classes.row}>
              <Typography variant="h6" className={classes.heading}>
                Edit Patient Preferred Rx Delivery Address
              </Typography>
            </Grid>
            <FieldArray
              name="addresses"
              formValues={formValues}
              preferredField="preferred_address_index"
              declineField="is_declined_to_provide_addresses"
              component={RenderAddressesForContacts}
              source="patient"
              change={(fieldName: string, fieldValue: any) => {
                if (fieldName === 'preferred_address_index') {
                  selectionIndex.current = fieldValue;
                }
                dispatch(change(FORM_ID, fieldName, fieldValue));
              }}
              classes={classes}
              idPrefix="patient_form"
              patient={patient}
              numberOfPotentialRxDeliveryAddresses={0}
            />
            <Grid item xs={12}>
              <ConfirmationPanel
                handleSubmit={submitWithPrechecks}
                handleCancel={handleCancel}
                disableSubmit={saveDisabled || !props.isDirty}
              />
            </Grid>
          </div>
        </form>
      </>
    </Modal>
  );
};

const mapStateToProps = (state: any): any => {
  const { patient, audit, contactList: contacts } = state;

  let preferredAddressIndex = 0;

  const stripDeleted = ({ deleted }: { deleted?: boolean }) => deleted === undefined || !deleted;

  const initialValues = {
    addresses: [
      ...(patient.addresses?.filter(stripDeleted).map((address: any) => ({
        ownerType: UNIQUE_CONTACT_TYPE_ID_FOR_PATIENT_KEY,
        ownerId: `${UNIQUE_CONTACT_TYPE_ID_FOR_PATIENT_KEY}_${patient.id}`,
        ...address,
      })) || [
        {
          ownerType: UNIQUE_CONTACT_TYPE_ID_FOR_PATIENT_KEY,
          ownerId: `${UNIQUE_CONTACT_TYPE_ID_FOR_PATIENT_KEY}_${patient.id}`,
        },
      ]),
      ...(Object.entries(contacts).reduce((acc, [contactId, contact]) => {
        // @ts-ignore
        if (contact.addresses?.filter(stripDeleted).length > 0) {
          // @ts-ignore
          const contactAddress =
            contact.addresses.filter(stripDeleted).map((address: any) => ({
              ownerType: UNIQUE_CONTACT_TYPE_ID_FOR_CONTACT_KEY,
              ownerId: `${UNIQUE_CONTACT_TYPE_ID_FOR_CONTACT_KEY}_${contactId}`,
              ...address,
            })) || [];
          return acc.concat(contactAddress);
        }
        return acc;
      }, []) || []),
    ],
    preferred_address_index: 0,
    is_declined_to_provide_addresses: 0,
    preferred_contact_method: patient.preferred_contact_method,
    preferred_rx_delivery_contact_id: patient.preferred_rx_delivery_contact_id,
    preferred_rx_delivery_entity_key: patient.preferred_rx_delivery_entity_key,
    preferred_rx_delivery_patient: patient.preferred_rx_delivery_patient,
  };

  if (patient.preferred_rx_delivery_patient) {
    preferredAddressIndex = initialValues.addresses.findIndex(
      addr => addr.ownerType === 'patient' && addr.key === patient.preferred_rx_delivery_entity_key,
    );
  } else {
    preferredAddressIndex = initialValues.addresses.findIndex(
      addr =>
        addr.ownerType === 'contact' &&
        Number((addr.ownerId || '_-1').split('_')[1]) ===
          patient.preferred_rx_delivery_contact_id &&
        addr.key === patient.preferred_rx_delivery_entity_key,
    );
  }

  initialValues.preferred_address_index = preferredAddressIndex;

  const formValues = getFormValues(FORM_ID)(state) || initialValues;

  return {
    form: FORM_ID,
    initialValues,
    patient,
    formValues,
    auditState: audit,
    enableReinitialize: true,
    isDirty: isDirty(FORM_ID)(state),
  };
};

export default compose<any, any>(
  withStyles(styles),
  connect(mapStateToProps),
  reduxForm({}),
)(EditContactAddressModal);
